const DEFAULT_LOGO_PATH = '/default-images/Logo/default_logo.png';
const LOGO_EXTENSIONS = ['.png', '.jpg', '.webp', '.gif', '.jpeg', '.svg', '.avif'];
const logoResolutionCache = new Map();
const imageAvailabilityCache = new Map();

const normalizeCollegeName = (value = '') => (
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
);

const getLogoNameVariations = (collegeName = '') => {
  const normalizedName = normalizeCollegeName(collegeName);
  const bracketName = collegeName.match(/\(([^)]+)\)/)?.[1]?.toLowerCase().trim();
  const firstWord = collegeName.split(' ')[0]?.toLowerCase().trim();
  const strippedName = normalizeCollegeName(
    collegeName.replace(/college|university|institute|school/g, '')
  );

  return [normalizedName, bracketName, firstWord, strippedName].filter(Boolean);
};

const canLoadImage = (url) => {
  if (!url) {
    return Promise.resolve(false);
  }

  if (imageAvailabilityCache.has(url)) {
    return imageAvailabilityCache.get(url);
  }

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

  imageAvailabilityCache.set(url, promise);
  return promise;
};

const resolveLocalLogo = async (collegeName) => {
  const variations = getLogoNameVariations(collegeName);

  for (const filename of variations) {
    for (const extension of LOGO_EXTENSIONS) {
      try {
        const logo = await import(`../assets/logos/${filename}${extension}`);
        return { mode: 'image', src: logo.default };
      } catch (error) {
        // Try the next candidate.
      }
    }
  }

  const defaultLogoAvailable = await canLoadImage(DEFAULT_LOGO_PATH);
  if (defaultLogoAvailable) {
    return { mode: 'image', src: DEFAULT_LOGO_PATH };
  }

  return { mode: 'shortName', src: null };
};

export const getShortCollegeName = (collegeName = '', shortName = '') => (
  shortName ||
  collegeName.split(' ').map((word) => word[0]).join('').slice(0, 4).toUpperCase() ||
  'COLLEGE'
);

export const resolveCollegeLogo = async (collegeName, logoUrl = '') => {
  const cacheKey = `${collegeName || ''}::${logoUrl || ''}`;
  if (logoResolutionCache.has(cacheKey)) {
    return logoResolutionCache.get(cacheKey);
  }

  const resolutionPromise = (async () => {
    if (logoUrl) {
      const isRemoteLogoAvailable = await canLoadImage(logoUrl);
      if (isRemoteLogoAvailable) {
        return { mode: 'image', src: logoUrl };
      }
    }

    return resolveLocalLogo(collegeName);
  })();

  logoResolutionCache.set(cacheKey, resolutionPromise);
  return resolutionPromise;
};
